import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import {
  AttributeType,
  LevelType,
  PositionAndDimensionsType,
  PositionType,
  SpaceType
} from 'data/types';
import {
  beginUndoBatch,
  endUndoBatch
} from 'features/structure/structure-history-middleware';
import {
  reconcileSpaceWallsRequested,
  wallReconcileRequestsFor
} from 'features/structure/structure-reconcile-actions';
import {
  addAutoLayoutSpaceIds,
  removeAutoLayoutSpaceIds,
  setStructure
} from 'features/structure/structure-slice';
import { produce } from 'immer';
import {
  collectLevelAutoLayoutSpaces,
  collectLevelUserPlacedSpaces,
  markAutoLayoutSpace
} from 'utils/auto-layout-space';
import {
  carveAutoLayoutSpace,
  LeftoverCarvePlan,
  LeftoverGeometry
} from 'utils/carve-auto-layout-space';
import {
  getEffectiveFootprintPolygons,
  legacyFootprintToPolygons
} from 'utils/footprint-polygons';
import type {
  RebuildLeftoverResult,
  ResizedSpaceGeometry
} from 'utils/rebuild-hybrid-leftover';
import { rebuildLeftoverFromOccupants } from 'utils/rebuild-hybrid-leftover';
import { createSection, createSpace } from 'utils/structure';

export const HYBRID_LEFTOVER_HOLE_MESSAGE =
  'Drop the room toward an edge of the leftover space so it does not leave a hole.';

export type { LeftoverCarvePlan };

export type PlanLeftoverCarvesResult =
  | { ok: true; plans: LeftoverCarvePlan[] }
  | { ok: false; reason: 'hole' | 'no-overlap' };

export function incomingSpacePatch(incoming: PositionAndDimensionsType) {
  return {
    position: { x: incoming.x, y: incoming.y },
    width: incoming.width,
    length: incoming.height
  };
}

export function leftoverGeometryPatch(geometry: LeftoverGeometry) {
  return {
    position: geometry.position,
    width: geometry.width,
    length: geometry.length,
    vertices: geometry.vertices
  };
}

export function createExtraLeftoverSpace(
  leftover: SpaceType,
  geometry: LeftoverGeometry,
  catalogAttributes: AttributeType[]
): SpaceType | null {
  const extra = createSpace(leftover.itemId, {
    attributesToInherit: leftover.attributes,
    data: {
      label: leftover.label,
      conditioned: leftover.conditioned,
      position: geometry.position,
      width: geometry.width,
      length: geometry.length,
      vertices: geometry.vertices
    }
  });
  if (!extra) {
    return null;
  }
  const marked = markAutoLayoutSpace(extra, catalogAttributes);
  return {
    ...marked,
    attributes: marked.attributes.map((attribute) =>
      attribute.code === 'square_footage'
        ? { ...attribute, value: geometry.squareFootage }
        : attribute
    )
  };
}

export function planLeftoverCarves(
  leftovers: SpaceType[],
  incoming: PositionAndDimensionsType
): PlanLeftoverCarvesResult {
  const plans: LeftoverCarvePlan[] = [];

  for (const leftover of leftovers) {
    const result = carveAutoLayoutSpace(leftover, incoming);
    if (!result.ok) {
      if (result.reason === 'hole') {
        return { ok: false, reason: 'hole' };
      }
    } else {
      plans.push({
        leftover,
        leftoverGeometry: result.leftover,
        extras: result.extras
      });
    }
  }

  if (plans.length === 0) {
    return { ok: false, reason: 'no-overlap' };
  }
  return { ok: true, plans };
}

function applySquareFootage(space: SpaceType, squareFootage: number) {
  const attribute = space.attributes.find(
    (entry) => entry.code === 'square_footage'
  );
  if (attribute) {
    attribute.value = squareFootage;
  }
}

function applyLeftoverPlansToLevel(
  level: LevelType,
  plans: LeftoverCarvePlan[],
  catalogAttributes: AttributeType[],
  extraIds: string[]
) {
  plans.forEach((plan) => {
    if (!plan.leftoverGeometry) {
      level.sections.forEach((section) => {
        const index = section.spaces.findIndex(
          (space) => space.uniqueId === plan.leftover.uniqueId
        );
        if (index >= 0) {
          section.spaces.splice(index, 1);
        }
      });
    } else {
      level.sections.forEach((section) => {
        const leftover = section.spaces.find(
          (space) => space.uniqueId === plan.leftover.uniqueId
        );
        if (!leftover) {
          return;
        }
        leftover.position = plan.leftoverGeometry!.position;
        leftover.width = plan.leftoverGeometry!.width;
        leftover.length = plan.leftoverGeometry!.length;
        leftover.vertices = plan.leftoverGeometry!.vertices;
        applySquareFootage(leftover, plan.leftoverGeometry!.squareFootage);
      });
    }

    plan.extras.forEach((extraGeometry) => {
      const extra = createExtraLeftoverSpace(
        plan.leftover,
        extraGeometry,
        catalogAttributes
      );
      if (!extra) {
        return;
      }
      extraIds.push(extra.uniqueId);
      level.sections.push(createSection({ spaces: [extra] }));
    });
  });
}

function dispatchLeftoverIdChanges(
  dispatch: Dispatch<UnknownAction>,
  plans: LeftoverCarvePlan[],
  extraIds: string[]
) {
  const consumedIds = plans
    .filter((plan) => !plan.leftoverGeometry)
    .map((plan) => plan.leftover.uniqueId);
  if (consumedIds.length > 0) {
    dispatch(removeAutoLayoutSpaceIds(consumedIds));
  }
  if (extraIds.length > 0) {
    dispatch(addAutoLayoutSpaceIds(extraIds));
  }
}

function dispatchWallReconcile(
  dispatch: Dispatch<UnknownAction>,
  previousStructure: LevelType[],
  plans: LeftoverCarvePlan[],
  extraIds: string[],
  resizedSpaceId?: string
) {
  const rebuiltIds = plans
    .filter((plan) => plan.leftoverGeometry)
    .map((plan) => plan.leftover.uniqueId);
  const spaceIds = [
    ...(resizedSpaceId ? [resizedSpaceId] : []),
    ...rebuiltIds,
    ...extraIds
  ];
  if (spaceIds.length > 0) {
    dispatch(
      reconcileSpaceWallsRequested(
        wallReconcileRequestsFor(previousStructure, spaceIds)
      )
    );
  }
}

export function planLevelLeftoverRebuild(
  level: LevelType,
  autoLayoutSpaceIds: readonly string[],
  fallbackFootprint?: PositionType[] | null,
  occupants?: SpaceType[]
): RebuildLeftoverResult {
  const leftovers = collectLevelAutoLayoutSpaces(level, autoLayoutSpaceIds);
  if (leftovers.length === 0) {
    return { ok: true, plans: [] };
  }

  const levelPolygons = getEffectiveFootprintPolygons(level);
  return rebuildLeftoverFromOccupants(
    levelPolygons.length > 0
      ? levelPolygons
      : legacyFootprintToPolygons(fallbackFootprint),
    leftovers,
    occupants ?? collectLevelUserPlacedSpaces(level, autoLayoutSpaceIds)
  );
}

export function applyLeftoverCarvePlans(
  dispatch: Dispatch<UnknownAction>,
  structure: LevelType[],
  levelId: string,
  plans: LeftoverCarvePlan[],
  catalogAttributes: AttributeType[]
): void {
  const extraIds: string[] = [];
  const newStructure = produce(structure, (draft) => {
    const level = draft.find((entry) => entry.uniqueId === levelId);
    if (!level) {
      return;
    }
    applyLeftoverPlansToLevel(level, plans, catalogAttributes, extraIds);
  });

  dispatch(beginUndoBatch());
  dispatch(setStructure(newStructure));
  dispatchLeftoverIdChanges(dispatch, plans, extraIds);
  dispatchWallReconcile(dispatch, structure, plans, extraIds);
  dispatch(endUndoBatch());
}

export function applyLevelLeftoverRebuild(
  dispatch: Dispatch<UnknownAction>,
  structure: LevelType[],
  levelId: string,
  autoLayoutSpaceIds: readonly string[],
  catalogAttributes: AttributeType[],
  fallbackFootprint?: PositionType[] | null
): boolean {
  const level = structure.find((entry) => entry.uniqueId === levelId);
  if (!level) {
    return false;
  }

  const rebuilt = planLevelLeftoverRebuild(
    level,
    autoLayoutSpaceIds,
    fallbackFootprint
  );
  if (!rebuilt.ok || rebuilt.plans.length === 0) {
    return false;
  }

  applyLeftoverCarvePlans(
    dispatch,
    structure,
    levelId,
    rebuilt.plans,
    catalogAttributes
  );
  return true;
}

export function applyResizedSpaceAndLeftoverPlans(
  dispatch: Dispatch<UnknownAction>,
  structure: LevelType[],
  levelId: string,
  resizedSpace: ResizedSpaceGeometry,
  plans: LeftoverCarvePlan[],
  catalogAttributes: AttributeType[]
): void {
  const extraIds: string[] = [];
  const newStructure = produce(structure, (draft) => {
    const level = draft.find((entry) => entry.uniqueId === levelId);
    if (!level) {
      return;
    }

    level.sections.forEach((section) => {
      const space = section.spaces.find(
        (entry) => entry.uniqueId === resizedSpace.uniqueId
      );
      if (!space) {
        return;
      }
      space.position = resizedSpace.position;
      space.width = resizedSpace.width;
      space.length = resizedSpace.length;
      space.vertices = resizedSpace.vertices;
      applySquareFootage(space, resizedSpace.squareFootage);
    });

    applyLeftoverPlansToLevel(level, plans, catalogAttributes, extraIds);
  });

  dispatch(beginUndoBatch());
  dispatch(setStructure(newStructure));
  dispatchLeftoverIdChanges(dispatch, plans, extraIds);
  dispatchWallReconcile(
    dispatch,
    structure,
    plans,
    extraIds,
    resizedSpace.uniqueId
  );
  dispatch(endUndoBatch());
}
